import { useState, type FormEvent } from 'react'
import { Pencil, Trash2, Users } from 'lucide-react'
import clsx from 'clsx'
import { Button } from '@/components/common/Button'
import { patchingApi, toPatchApiError } from '@/services/patching'
import type { VmTag } from '@/types/patching'
import { validateTagName } from '@/utils/patchValidation'
import styles from './Patching.module.css'

type Panel = 'rename' | 'members' | 'delete'

interface TagManagerProps {
  tags: VmTag[]
  /** Called after every change, so the page can reload the tags and the Tags column. */
  onChanged: () => Promise<void> | void
  onMessage: (message: string) => void
}

/**
 * Every tag of the open connection: its VMs, rename, remove VMs, and delete. A tag that an active campaign was made from
 * cannot be deleted (the server refuses too); a campaign keeps its own copy of the VMs either way.
 */
export function TagManager({ tags, onChanged, onMessage }: TagManagerProps) {
  const [open, setOpen] = useState<{ id: number; panel: Panel } | null>(null)
  const [newName, setNewName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<{ id: number; message: string } | null>(null)

  const toggle = (id: number, panel: Panel, tag?: VmTag) => {
    setError(null)
    if (open?.id === id && open.panel === panel) {
      setOpen(null)
      return
    }
    setNewName(panel === 'rename' && tag ? tag.name : '')
    setOpen({ id, panel })
  }

  async function act(id: number, action: () => Promise<string>) {
    setBusy(true)
    setError(null)
    try {
      onMessage(await action())
      await onChanged()
    } catch (err) {
      setError({ id, message: toPatchApiError(err).message })
    } finally {
      setBusy(false)
    }
  }

  async function rename(event: FormEvent, tag: VmTag) {
    event.preventDefault()
    const problems = validateTagName(newName)
    if (problems.length > 0) {
      setError({ id: tag.id, message: problems[0] })
      return
    }
    if (newName === tag.name) {
      setOpen(null)
      return
    }
    await act(tag.id, async () => {
      await patchingApi.updateTag(tag.id, { name: newName })
      setOpen(null)
      return `Renamed “${tag.name}” to “${newName}”. Campaigns already created from it keep the name they were created with.`
    })
  }

  if (tags.length === 0) {
    return (
      <section aria-label="Manage tags" className={styles.stack}>
        <p className={styles.muted}>No tags yet. Select VMs in the table and click “Add tags” to make one.</p>
      </section>
    )
  }

  return (
    <section aria-label="Manage tags" className={styles.stack}>
      {tags.map((tag) => {
        const panel = open?.id === tag.id ? open.panel : null
        const inUse = tag.used_by.length > 0
        const own = error?.id === tag.id ? error.message : null
        return (
          <article key={tag.id} className={styles.tagCard} aria-label={`Tag ${tag.name}`}>
            <div className={styles.tagCardHead}>
              <div>
                <strong className={styles.mono}>{tag.name}</strong>
                {tag.description && <span className={styles.muted}> · {tag.description}</span>}
                <div className={styles.muted}>
                  <span data-testid={`tag-count-${tag.name}`}>{tag.member_count} VM{tag.member_count === 1 ? '' : 's'}</span>
                  {tag.resource_groups.length > 0 && <> · resource group{tag.resource_groups.length === 1 ? '' : 's'} {tag.resource_groups.join(', ')}</>}
                </div>
                {inUse && (
                  <div className={styles.hint} data-testid={`tag-in-use-${tag.name}`}>
                    In use by active campaign{tag.used_by.length === 1 ? '' : 's'} {tag.used_by.join(', ')}.
                  </div>
                )}
              </div>
              <div className={styles.actionRow}>
                <Button variant="secondary" size="sm" icon={<Pencil size={14} />} aria-label={`Rename ${tag.name}`} aria-expanded={panel === 'rename'} disabled={busy} onClick={() => toggle(tag.id, 'rename', tag)}>
                  Rename
                </Button>
                <Button variant="secondary" size="sm" icon={<Users size={14} />} aria-label={`Remove VMs from ${tag.name}`} aria-expanded={panel === 'members'} disabled={busy} onClick={() => toggle(tag.id, 'members')}>
                  Remove VMs
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  icon={<Trash2 size={14} />}
                  aria-label={`Delete ${tag.name}`}
                  aria-expanded={panel === 'delete'}
                  disabled={busy || inUse}
                  title={inUse ? `Used by active campaign ${tag.used_by.join(', ')}: finish or cancel it first.` : undefined}
                  onClick={() => toggle(tag.id, 'delete')}
                >
                  Delete
                </Button>
              </div>
            </div>

            {panel === 'rename' && (
              <form className={styles.inlineRow} onSubmit={(event) => void rename(event, tag)} noValidate>
                <label className={styles.label} htmlFor={`rename-${tag.id}`}>New name for {tag.name}</label>
                <input
                  id={`rename-${tag.id}`}
                  className={clsx(styles.input, own && styles.inputError)}
                  value={newName}
                  autoComplete="off"
                  spellCheck={false}
                  onChange={(event) => setNewName(event.target.value)}
                />
                <Button type="submit" size="sm" disabled={busy}>Save name</Button>
                <Button type="button" size="sm" variant="secondary" onClick={() => setOpen(null)} disabled={busy}>Cancel</Button>
              </form>
            )}

            {panel === 'members' && (
              <ul className={styles.memberList} aria-label={`VMs in ${tag.name}`}>
                {tag.members.length === 0 && <li className={styles.muted}>This tag has no VMs.</li>}
                {tag.members.map((member) => (
                  <li key={member.id} className={styles.memberRow}>
                    <span>
                      <span className={styles.mono}>{member.vm_name}</span>
                      <span className={styles.muted}> · {member.resource_group} · {member.region}</span>
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Remove ${member.vm_name} from ${tag.name}`}
                      disabled={busy}
                      onClick={() =>
                        void act(tag.id, async () => {
                          await patchingApi.removeTagMember(tag.id, member.id)
                          return `Removed ${member.vm_name} from “${tag.name}”. Campaigns already created from it are not affected.`
                        })
                      }
                    >
                      Remove
                    </Button>
                  </li>
                ))}
              </ul>
            )}

            {panel === 'delete' && (
              <div className={clsx(styles.alert, styles.alertError)} role="alertdialog" aria-label={`Confirm delete ${tag.name}`}>
                <div className={styles.stack}>
                  <p style={{ margin: 0 }}>
                    Delete the tag <strong>{tag.name}</strong>? Its {tag.member_count} VM{tag.member_count === 1 ? '' : 's'} are untagged. The VMs
                    and Azure itself are not changed, and campaigns already created from the tag keep their own list.
                  </p>
                  <div className={styles.actionRow}>
                    <Button
                      variant="danger"
                      size="sm"
                      disabled={busy}
                      loading={busy}
                      onClick={() =>
                        void act(tag.id, async () => {
                          await patchingApi.deleteTag(tag.id)
                          setOpen(null)
                          return `Deleted the tag “${tag.name}”.`
                        })
                      }
                    >
                      Yes, delete this tag
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => setOpen(null)} disabled={busy}>Cancel</Button>
                  </div>
                </div>
              </div>
            )}

            {own && <div className={clsx(styles.alert, styles.alertError)} role="alert">{own}</div>}
          </article>
        )
      })}
    </section>
  )
}
